import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Snackbar, Alert, Button } from '@mui/material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { DataProviderInstance } from '../../api/DataProvider';
import AreaTitle from '../../components/areaTitle/AreaTitle';
import CustomButton from '../../components/buttons/CustomButton';
import CreateEditLeadPopup from '../../popup/CreateEditLeadPopup/CreateEditLeadPopup';
import CreatePatientPopup from '../../popup/CreatePatientPopup/CreatePatientPopup';
import DateRangeHeader from '../../components/dateRangeHeader/DateRangeHeader';
import Toast from '../../components/support/Toast';
import { ExportToExcel } from '../../utils/Utils';
import { buildPatientPrefillFromLead } from '../../utils/leadUtils';
import { Channel_Whatsapp } from '../../consts/LeadsConfig';
import { SendWhatsAppMessage } from '../../api/WhatsappAPI';
import MessagesTemplate from '../../consts/MessagesTemplate';
import usePlanLock from '../../hooks/usePlanLock';
import { SUBSCRIPTION_PERMISSIONS } from '../../consts/SubscriptionFeatures';
import LeadCardItem from './LeadCardItem';
import LeadsBoard from './components/LeadsBoard';
import LeadsToolbar from './components/LeadsToolbar';
import LeadsEmptyState from './components/LeadsEmptyState';
import { LEAD_STAGES, INVALID_STAGE } from './components/leadStages';
import styles from './Leads.module.css';

// "YYYY-MM-DD" como data local (o mês começa em 0)
const parseDate = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

function Leads() {
  const { CompanyConfig, GetLeads, GetPatientById } = DataProviderInstance();

  // Cadeado por plano: os leads continuam visíveis; criar, editar e mudar de
  // status abrem o modal de upgrade. Converter em paciente e WhatsApp continuam.
  const lock = usePlanLock(SUBSCRIPTION_PERMISSIONS.LEADS_FUNNEL, 'O funil de leads');
  const [loaded, setLoaded] = useState(false);
  const [leads, setLeads] = useState([]);

  const [currentLead, setCurrentLead] = useState(null);
  const [createOrEditPopupVisibility, setCreateOrEditPopupVisibility] = useState(false);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [needsReload, setNeedsReload] = useState(false);

  const [viewInvalid, setViewInvalid] = useState(false);
  const [phoneSearch, setPhoneSearch] = useState('');

  const [patientPrefill, setPatientPrefill] = useState(null);
  const [createPatientPopupVisibility, setCreatePatientPopupVisibility] = useState(false);
  const [createdPatientId, setCreatedPatientId] = useState(null);
  const [toastMessage, setToastMessage] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    setNeedsReload(true);
  }, []);

  useEffect(() => {
    if (needsReload) {
      setLoaded(false);
      LoadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsReload]);

  const LoadData = () => {
    GetLeads((result) => {
      setLeads(result.data);
      setLoaded(true);
      setNeedsReload(false);
    });
  };

  const filtered = useMemo(() => {
    const startDateParsed = parseDate(startDate);
    if (startDateParsed) startDateParsed.setHours(0, 0, 0, 0);

    const endDateParsed = parseDate(endDate);
    if (endDateParsed) endDateParsed.setHours(23, 59, 59, 999);

    return (leads || []).filter((lead) => {
      const leadDate = new Date(lead.createdAt);
      const dateFilter = (
        (!startDateParsed || leadDate >= startDateParsed) &&
        (!endDateParsed || leadDate <= endDateParsed)
      );

      const phoneFilter = !phoneSearch ||
        lead.phone.toLowerCase().includes(phoneSearch.toLowerCase().replace(/\D/g, ''));

      return dateFilter && phoneFilter;
    });
  }, [startDate, endDate, leads, phoneSearch]);

  // Inválidos ficam numa coluna própria (só com "Mostrar inválidos"); os demais, pela etapa
  const columns = useMemo(() => {
    const byStage = LEAD_STAGES.map((stage) => ({
      stage,
      leads: filtered.filter((lead) => lead.valid != '0' && lead.status == stage.key),
    }));
    if (!viewInvalid) return byStage;
    return [...byStage, { stage: INVALID_STAGE, leads: filtered.filter((lead) => lead.valid == '0') }];
  }, [filtered, viewInvalid]);

  const renderCard = (leadData) => (
    <LeadCardItem
      key={leadData.id}
      leadData={leadData}
      countryCode={getLeadCountryCode(leadData)}
      onEdit={lock.guard(() => showCreateEditLeadPopup(leadData))}
      onUpdate={() => setNeedsReload(true)}
      onBlockedAction={lock.locked ? lock.openUpgrade : null}
      onWhatssappMessageClick={() => onWhatssappMessageClickHandler(leadData)}
      onConvertToPatient={() => onConvertToPatientClickHandler(leadData)}
    />
  );

  function onWhatssappMessageClickHandler(leadData) {
    if (!CompanyConfig) return;

    let message = ''; // Empty message to allow customization
    SendWhatsAppMessage(leadData.phone, MessagesTemplate.ConvertToWhatsAppText(message), getLeadCountryCode(leadData));
  }

  // company_leads não tem coluna de DDI. Leads vindos do WhatsApp (channel "whatsapp")
  // já chegam com o número internacional, então não deve ser prefixado; nos demais
  // casos usamos o DDI do paciente vinculado ou o padrão da empresa.
  function getLeadCountryCode(leadData) {
    if (leadData.channel === Channel_Whatsapp) return '';
    return (leadData.patientId ? GetPatientById(leadData.patientId)?.countryCode : null) || CompanyConfig?.countryCode || '55';
  }

  function showCreateEditLeadPopup(leadData) {
    setCurrentLead(leadData);
    setCreateOrEditPopupVisibility(true);
  }

  function onCreateEditLeadPopupHide() {
    setCurrentLead(null);
    setCreateOrEditPopupVisibility(false);
  }

  // `message`: aviso de sucesso do popup (lead criado, salvo ou excluído)
  function onLeadUpdate(message) {
    setNeedsReload(true);
    if (message) setToastMessage(message);
  }

  function onCreateLeadClickHandler() {
    setCurrentLead(null);
    setCreateOrEditPopupVisibility(true);
  }

  // Conversão do lead em paciente sem sair do kanban
  function onConvertToPatientClickHandler(leadData) {
    setPatientPrefill(buildPatientPrefillFromLead(leadData, leadData.phone));
    setCreatePatientPopupVisibility(true);
  }

  function onCreatePatientPopupHide() {
    setCreatePatientPopupVisibility(false);
    setPatientPrefill(null);
  }

  function onPatientCreatedHandler(response) {
    setNeedsReload(true);

    const patientId = response?.data?.patientId;
    if (patientId) {
      setCreatedPatientId(patientId);
    }
  }

  // Exportação para planilha (sem botão na tela hoje; mantida como estava)
  // eslint-disable-next-line no-unused-vars
  function onExportExcelClickHandler() {
    let data = [...filtered];

    data = data.map(({ id, companyId, phone, name, sourceId, updatedAt, updatedBy, ...rest }) => {
      // Handle case where campaignId might not exist in the campaigns list
      let campaignId = rest.campaignId || -1;
      let campaign = CompanyConfig.LeadsCampaigns.find(x => x.id == campaignId + '');
      let campaignName = campaign?.name || 'Desconhecida';
      let source = campaign ?
        (CompanyConfig.LeadsSources.find(x => x.id == campaign.sourceId + '')?.name || 'Desconhecida') :
        'Desconhecida';
      // Nome e telefone são as primeiras colunas da planilha
      return { name, phone, ...rest, source, campaignName };
    });

    ExportToExcel(data, 'Leads', 'leads-' + startDate + '-' + endDate);
  }

  const handleDateChange = (initialDate, finalDate) => {
    setStartDate(initialDate);
    setEndDate(finalDate);
  };

  const onAddClick = lock.guard(onCreateLeadClickHandler);
  const isEmpty = loaded && filtered.length === 0;
  // Esqueleto só na primeira carga; recargas (mudar etapa, salvar) mantêm o quadro na tela
  const firstLoad = !loaded && (leads || []).length === 0;

  return (
    <div className='page-content'>
      <div className={styles.container}>
        <div className={styles.page}>
          <div className={styles.header}>
            <AreaTitle
              Title='Leads'
              Description='Contatos interessados, do primeiro contato ao agendamento.'
              Icon={<GroupsOutlinedIcon />}
            />
            <CustomButton
              variant="contained"
              style="main"
              icon={lock.locked ? <LockOutlinedIcon /> : <AddRoundedIcon />}
              label="Novo lead"
              onClick={onAddClick}
            />
          </div>

          {lock.banner}
          {lock.modal}

          <div className={styles.period}>
            <DateRangeHeader onChange={handleDateChange} />
          </div>

          <section className={styles.funnel}>
            <LeadsToolbar
              total={filtered.length}
              startDate={startDate}
              endDate={endDate}
              viewInvalid={viewInvalid}
              onViewInvalidChange={setViewInvalid}
              phoneSearch={phoneSearch}
              onPhoneSearchChange={setPhoneSearch}
            />

            {isEmpty ? (
              <LeadsEmptyState searching={!!phoneSearch} onAdd={onAddClick} />
            ) : (
              <LeadsBoard
                columns={columns}
                loading={firstLoad}
                renderCard={renderCard}
                skeletonCount={columns.length}
              />
            )}
          </section>
        </div>
      </div>

      {/* Fora do container: a contenção do @container prenderia o Snackbar (position: fixed) */}
      {createOrEditPopupVisibility && (
        <CreateEditLeadPopup
          OnHide={onCreateEditLeadPopupHide}
          OnUpdate={onLeadUpdate}
          Data={currentLead}
        />
      )}

      {createPatientPopupVisibility && (
        <CreatePatientPopup
          Data={patientPrefill}
          Visibility={true}
          OnChange={onPatientCreatedHandler}
          OnHide={onCreatePatientPopupHide}
        />
      )}

      <Snackbar
        open={createdPatientId != null}
        autoHideDuration={8000}
        onClose={() => setCreatedPatientId(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          variant="filled"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate(`/patients/${createdPatientId}`)}
            >
              Ver paciente
            </Button>
          }
        >
          Paciente cadastrado a partir do lead.
        </Alert>
      </Snackbar>

      <Toast
        open={!!toastMessage}
        onClose={() => setToastMessage('')}
        message={toastMessage}
        severity="success"
        autoHideDuration={4000}
      />
    </div>
  );
}

export default Leads;
